import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, CircularProgress, Divider, IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { getSchedulerConfig, saveSchedulerConfig } from './api/tumblerApi';
import normalizeApiError from './utils/normalizeApiError';
import { SOLVER_NUMERIC_FIELDS, splitSolverConfig, mergeSolverConfig } from './utils/solverConfigSchema';

// Fixes ASSESSMENT.md P1/P2 findings for the old TumblerScheduling.jsx config section:
//  - "PATCH จะแทนค่า JSON configuration ทั้งก้อน จึงลบ shift_windows และ top-level keys อื่นได้"
//    -> we keep the *original* configuration object we loaded and only overwrite pot_mapping and
//       the known solver_config fields inside it, so shift_windows / unknown top-level keys and
//       nested objective_weights/cleaning_matrix/fixed_pot_intervals survive a save untouched.
//  - "Object.entries(solverConfig) สร้าง number input ทุก key" -> replaced with an explicit typed
//    field list (SOLVER_NUMERIC_FIELDS); anything else in solver_config is preserved as `unknown`
//    and merged back in rather than rendered/coerced.
//  - "โหลด config ตาม plant/factory แต่ถ้าไม่มี record จะ return โดยไม่ reset ค่าเดิม" and
//    "setScenarios ถูกเรียกก่อนตรวจ active ใน effect" -> request cancellation + resetting state
//    to defaults whenever plant/factory changes, before the new load resolves.
export default function SchedulerSettings({ plant, factory }) {
  const accessToken = useSelector((state) => state.auth.access_token);
  const [configId, setConfigId] = useState(null);
  const [originalConfiguration, setOriginalConfiguration] = useState({});
  const [solverKnown, setSolverKnown] = useState(() => splitSolverConfig({}).known);
  const [solverUnknown, setSolverUnknown] = useState({});
  const [potMapping, setPotMapping] = useState([{ potId: '1', machineName: '' }]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const requestKeyRef = useRef(0);
  const controllerRef = useRef(null);
  const [shiftWindows, setShiftWindows] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    if (!accessToken || !plant || !factory) return;
    const key = ++requestKeyRef.current;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoadFailed(false);
    setShiftWindows([]);
    // Reset immediately so a slow response for a previous plant/factory can never be applied
    // to the newly selected scope once it arrives late.
    setLoading(true);
    setMessage(null);
    setConfigId(null);
    setOriginalConfiguration({});
    setSolverKnown(splitSolverConfig({}).known);
    setSolverUnknown({});
    setPotMapping([{ potId: '1', machineName: '' }]);
    try {
      const response = await getSchedulerConfig({ plant, factory, accessToken, signal: controller.signal });
      if (requestKeyRef.current !== key) return;
      const records = Array.isArray(response.data) ? response.data : response.data.results || [];
      const record = records[0];
      if (record) {
        const configuration = record.configuration || {};
        const { known, unknown } = splitSolverConfig(configuration.solver_config);
        setConfigId(record.id);
        setOriginalConfiguration(configuration);
        setShiftWindows(Object.entries(configuration.shift_windows || {}).map(([code, window]) => ({ ...window, code })));
        setSolverKnown(known);
        setSolverUnknown(unknown);
        const mapping = Object.entries(configuration.pot_mapping || {}).map(([potId, machineName]) => ({ potId, machineName }));
        setPotMapping(mapping.length ? mapping : [{ potId: '1', machineName: '' }]);
      }
    } catch (error) {
      if (error?.code === 'ERR_CANCELED') return;
      if (requestKeyRef.current !== key) return;
      setLoadFailed(true);
      setMessage({ severity: 'error', text: normalizeApiError(error, 'Unable to load scheduler configuration.').summary });
    } finally {
      if (requestKeyRef.current === key) setLoading(false);
    }
    return () => controller.abort();
  }, [accessToken, plant, factory]);

  useEffect(() => { load(); const counter = requestKeyRef; const controller = controllerRef.current; return () => { ++counter.current; controller?.abort(); }; }, [load]);

  const changeSolverValue = (field, value) => {
    setSolverKnown((current) => ({ ...current, [field]: value === '' ? '' : Number(value) }));
  };

  const changePot = (index, field, value) => {
    setPotMapping((current) => current.map((pot, potIndex) => (potIndex === index ? { ...pot, [field]: value } : pot)));
  };

  const save = async () => {
    if (!accessToken) return;
    const mapping = Object.fromEntries(
      potMapping.filter((pot) => pot.potId && pot.machineName).map((pot) => [pot.potId, pot.machineName]),
    );
    if (Object.keys(mapping).length !== potMapping.length) {
      setMessage({ severity: 'error', text: 'Every pot needs a machine name.' });
      return;
    }

    const positive = ['num_pots', 'horizon_min', 'bin_capacity_kg', 'room_capacity_kg', 'time_step_min', 'solve_time_limit_s'];
    if (Object.entries(solverKnown).some(([key, value]) => value === '' || !Number.isFinite(Number(value)) || Number(value) < 0 || (positive.includes(key) && Number(value) <= 0)) || !Number.isInteger(Number(solverKnown.num_pots))) {
      setMessage({ severity: 'error', text: 'Enter valid nonnegative solver values; pots, horizon, capacities, time step and solve limit must be positive.' }); return;
    }
    if (!shiftWindows.length || new Set(shiftWindows.map((row) => row.code.trim())).size !== shiftWindows.length || shiftWindows.some((row) => !row.code.trim() || ['line_start_min', 'need_start_min'].some((field) => row[field] === '' || !Number.isFinite(Number(row[field])) || Number(row[field]) < 0))) {
      setMessage({ severity: 'error', text: 'Add unique shift codes and nonnegative start offsets for every SAP shift used.' }); return;
    }
    setSaving(true);
    setMessage(null);
    try {
      // Immutable merge: start from the original configuration object exactly as loaded, then
      // overwrite only pot_mapping and the solver_config fields this form edits. Everything else
      // (shift_windows, unrelated top-level keys) passes through unchanged.
      const payload = {
        plant_id: plant,
        factory,
        is_active: true,
        configuration: {
          ...originalConfiguration,
          pot_mapping: mapping,
          shift_windows: Object.fromEntries(shiftWindows.map(({ code, ...window }) => [code.trim(), { ...window, line_start_min: Number(window.line_start_min), need_start_min: Number(window.need_start_min) }])),
          solver_config: mergeSolverConfig(solverKnown, solverUnknown),
        },
      };
      const response = await saveSchedulerConfig({ configId, payload, accessToken });
      setConfigId(response.data.id);
      setOriginalConfiguration(response.data.configuration || payload.configuration);
      setMessage({ severity: 'success', text: 'Scheduler configuration saved.' });
    } catch (error) {
      setMessage({ severity: 'error', text: normalizeApiError(error, 'Unable to save scheduler configuration.').summary });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 240 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Typography color="text.secondary">
          Editing the fields below. Objective weights, cleaning matrix, and any other keys already
          in this configuration are preserved as-is on save.
        </Typography>
        <Button variant="contained" startIcon={<SaveOutlinedIcon />} onClick={save} disabled={saving || loadFailed}>
          {saving ? 'Saving…' : 'Save configuration'}
        </Button>
      </Stack>

      {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{ mb: 2 }}>{message.text}</Alert>}

      <Typography variant="h6">SAP shift windows</Typography>
      <Typography variant="body2">Offsets in minutes from planning start (Bangkok). Codes must match SAP shifts, e.g. D or N.</Typography>
      <Stack spacing={2} sx={{ my: 2 }}>
        {shiftWindows.map((row, index) => <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          {['code', 'line_start_min', 'need_start_min'].map((field) => <TextField key={field} label={field} type={field === 'code' ? 'text' : 'number'} value={row[field] ?? ''} onChange={(event) => setShiftWindows((current) => current.map((item, i) => i === index ? { ...item, [field]: event.target.value } : item))} size="small" />)}
          <Button onClick={() => setShiftWindows((current) => current.filter((_, i) => i !== index))}>Remove</Button>
        </Stack>)}
        <Button onClick={() => setShiftWindows((current) => [...current, { code: '', line_start_min: 0, need_start_min: 0 }])}>Add shift</Button>
      </Stack>
      <Typography variant="h6">Capacity and cleaning</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mt: 2 }}>
        {SOLVER_NUMERIC_FIELDS.map(({ key, label, unit }) => (
          <TextField
            key={key}
            label={`${label} (${unit})`}
            type="number"
            value={solverKnown[key] ?? ''}
            onChange={(event) => changeSolverValue(key, event.target.value)}
            size="small"
            fullWidth
          />
        ))}
      </Box>
      {Object.keys(solverUnknown).length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {Object.keys(solverUnknown).length} additional solver key(s) ({Object.keys(solverUnknown).join(', ')}) are kept
          unchanged — edit those on the backend/config JSON directly until this form supports them.
        </Typography>
      )}

      <Divider sx={{ my: 4 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Pot to machine mapping</Typography>
        <Tooltip title="Add pot">
          <IconButton onClick={() => setPotMapping((current) => [...current, { potId: String(current.length + 1), machineName: '' }])}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <Stack spacing={1.5} sx={{ mt: 2 }}>
        {potMapping.map((pot, index) => (
          <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
            <TextField label="Pot" value={pot.potId} onChange={(event) => changePot(index, 'potId', event.target.value)} size="small" sx={{ width: { sm: 140 } }} />
            <TextField label="Machine name" value={pot.machineName} onChange={(event) => changePot(index, 'machineName', event.target.value)} size="small" fullWidth />
            <Tooltip title="Remove pot">
              <span>
                <IconButton disabled={potMapping.length === 1} onClick={() => setPotMapping((current) => current.filter((_, potIndex) => potIndex !== index))}>
                  <DeleteOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
